import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import FoundingMemberCallout from "@/components/FoundingMemberCallout";

/**
 * Honest comparison page: FrazzleCRM vs GoHighLevel (agency wedge).
 * Rule: concede what GHL genuinely does better — credibility converts.
 * Pricing facts checked 2026-07 against gohighlevel.com/pricing.
 */

const ROWS: [string, string, string][] = [
  ["Starting price", "$0 (free forever tier)", "$97/mo (3 sub-accounts)"],
  ["Agency tier", "$199/mo Scale — unlimited workspaces & users", "$297/mo Unlimited (+$497 for SaaS mode)"],
  ["Usage fees", "None — bring your own SMTP/Twilio, pay them directly at cost", "Email, SMS, calls & AI billed on top (typically $20–150/mo)"],
  ["Automation model", "Visual map = the live automation, two-way sync", "Workflow builder, separate from funnel diagrams"],
  ["Email deliverability", "Your own domain & SMTP from day one", "Platform sending; frequent setup pain reported by switchers"],
  ["AI / MCP", "Built-in MCP server — Claude/ChatGPT can build & audit flows", "AI add-ons (billed per use); no agent-level API"],
  ["Learning curve", "One canvas; drawing is the interface", "20+ modules; famously steep first month"],
  ["Phone system, website builder, SaaS-mode reselling, mobile app", "Not included — we do funnels, email/SMS automation & CRM", "Included — this is GHL's real strength"],
];

export default function VsGoHighLevel() {
  return (
    <SiteShell
      title="FrazzleCRM vs GoHighLevel (2026) — an honest comparison"
      description="FrazzleCRM vs GoHighLevel, honestly: GHL is an all-in-one agency suite with a steep learning curve and usage fees. FrazzleCRM is the visual-first alternative — the funnel map IS the automation, $199 for unlimited workspaces, no usage fees."
    >

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-6xl px-6 pb-12 pt-16 text-center">
          <h1 className="mx-auto max-w-3xl text-[36px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[44px]">
            FrazzleCRM vs GoHighLevel
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600">
            Honest version: they&apos;re different tools. GoHighLevel is an
            all-in-one agency suite — phones, websites, SaaS reselling.
            FrazzleCRM does one thing obsessively well: funnels and
            automations you can actually <em>see</em>, at a price that
            doesn&apos;t scale against you.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
          <table className="w-full min-w-[640px] border-collapse text-left text-[13.5px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 text-[12.5px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold"> </th>
                <th className="px-4 py-3 font-bold text-indigo-700">FrazzleCRM</th>
                <th className="px-4 py-3 font-semibold">GoHighLevel</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, us, them]) => (
                <tr key={label} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3.5 font-medium text-slate-700">{label}</td>
                  <td className="px-4 py-3.5 text-slate-700">{us}</td>
                  <td className="px-4 py-3.5 text-slate-500">{them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-center text-[12px] text-slate-400">
          GoHighLevel pricing as published July 2026; check their site for
          current numbers.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-[18px] font-bold text-slate-900">
              Choose GoHighLevel if…
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-600">
              <li>• You want phones, websites, calendars and CRM in ONE login</li>
              <li>• You resell software to clients under your own brand (SaaS mode)</li>
              <li>• You have a team member who can own a real learning curve</li>
              <li>• You&apos;re all-in — GHL rewards full migration, not blending</li>
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-6 shadow-sm">
            <h2 className="text-[18px] font-bold text-slate-900">
              Choose FrazzleCRM if…
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-700">
              <li>• Your clients pay you for funnels and follow-up, not phone systems</li>
              <li>• You want the diagram you show clients to BE the thing that runs</li>
              <li>• Deliverability matters: your domain, your SMTP, no shared pools</li>
              <li>• You want AI (Claude, ChatGPT) building drafts you approve</li>
              <li>• $199 flat for unlimited client workspaces beats $297 + usage</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <h2 className="text-[22px] font-bold text-slate-900">
            The agency math
          </h2>
          <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">
            10 client workspaces on GoHighLevel Unlimited: $297/mo plus
            usage fees that grow with every send — call it $400–450/mo in
            practice. The same 10 clients on FrazzleCRM Scale: $199/mo
            flat, sending through each client&apos;s own domain at provider
            cost. And when a client asks &ldquo;what exactly runs when someone
            books?&rdquo;, you share the map — it&apos;s a{" "}
            <Link href="/templates" className="font-medium text-indigo-600 hover:text-indigo-500">
              live diagram
            </Link>
            , not a screenshot that went stale in March.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pt-14">
        <FoundingMemberCallout note="GoHighLevel's $97–$497/mo never stops. This payment does — you still bring your own SMTP and Twilio, billed at their cost, not ours." />
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center">
        <h2 className="text-[26px] font-bold text-slate-900">
          Try the map-first way — free.
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[14px] text-slate-600">
          5 flows, 1 map, 500 contacts, no card. Clone a{" "}
          <Link href="/templates" className="font-medium text-indigo-600 hover:text-indigo-500">
            template
          </Link>{" "}
          and see your first funnel run in minutes.
        </p>
        <a
          href={`${APP_URL}/signup`}
          className="mt-6 inline-block rounded-xl bg-indigo-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
        >
          Start free
        </a>
      </section>
    </SiteShell>
  );
}
